import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../platform/storage';
import { speechEngine } from '../platform/speech';
import {
  DEFAULT_POLISH_SPEECH_FILTERS,
  PolishSpeechFilters,
  PolishSpeechFragment,
  PolishSpeechQueue,
  shouldStopAfterFragment,
} from './polishSpeechQueue';

export type PolishSpeechState = 'idle' | 'speaking' | 'paused' | 'stopped';
export type PolishVoiceStatus = 'checking' | 'available' | 'missing';

const SPEED_STORAGE_KEY = '@planszowki/polish-speech-rate';
const SETTINGS_STORAGE_KEY = '@planszowki/polish-speech-settings';
const BATCH_SIZE = 12;
export const POLISH_SPEECH_RATES = [0.75, 1, 1.25, 1.5] as const;
export type PolishSpeechRate = (typeof POLISH_SPEECH_RATES)[number];

export type PolishSpeechSettings = {
  pitch: number;
  rememberProgress: boolean;
  autoAdvanceChapter: boolean;
  filters: PolishSpeechFilters;
};

const defaultSettings: PolishSpeechSettings = {
  pitch: 1,
  rememberProgress: true,
  autoAdvanceChapter: false,
  filters: DEFAULT_POLISH_SPEECH_FILTERS,
};

export function usePolishTextToSpeech() {
  const [state, setState] = useState<PolishSpeechState>('idle');
  const [speed, setSpeedState] = useState<PolishSpeechRate>(1);
  const [voiceStatus, setVoiceStatus] = useState<PolishVoiceStatus>('checking');
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [settings, setSettingsState] = useState<PolishSpeechSettings>(defaultSettings);
  const voiceId = useRef<string | undefined>(undefined);
  const queue = useRef<PolishSpeechQueue | null>(null);
  const batch = useRef<readonly PolishSpeechFragment[]>([]);
  const batchStart = useRef(0);
  const chunkIndex = useRef(0);
  const stopAfterIndex = useRef<number | null>(null);
  const generation = useRef(0);

  const stop = useCallback(async () => {
    generation.current += 1;
    stopAfterIndex.current = null;
    await speechEngine.stop();
    setState('stopped');
  }, []);

  const checkVoice = useCallback(async () => {
    try {
      const voices = await speechEngine.getAvailableVoices();
      const polishVoice = voices.find((voice) =>
        voice.language.replace('_', '-').toLowerCase().startsWith('pl')
      );
      voiceId.current = polishVoice?.identifier;
      const available =
        Boolean(polishVoice) ||
        (speechEngine.runtime === 'web' &&
          typeof window !== 'undefined' &&
          'speechSynthesis' in window);
      setVoiceStatus(available ? 'available' : 'missing');
      return available;
    } catch {
      setVoiceStatus('missing');
      return false;
    }
  }, []);

  useEffect(() => {
    void checkVoice();
    void storage.getItem(SPEED_STORAGE_KEY).then((stored) => {
      const parsed = Number(stored);
      if (POLISH_SPEECH_RATES.includes(parsed as PolishSpeechRate)) {
        setSpeedState(parsed as PolishSpeechRate);
      }
    });
    void storage.getItem(SETTINGS_STORAGE_KEY).then((stored) => {
      if (!stored) return;
      try {
        const parsed = JSON.parse(stored) as Partial<PolishSpeechSettings>;
        setSettingsState({
          pitch: typeof parsed.pitch === 'number' ? parsed.pitch : defaultSettings.pitch,
          rememberProgress: parsed.rememberProgress !== false,
          autoAdvanceChapter: parsed.autoAdvanceChapter === true,
          filters: { ...DEFAULT_POLISH_SPEECH_FILTERS, ...parsed.filters },
        });
      } catch {
        // Ignore a legacy or invalid local preference.
      }
    });
    return () => {
      generation.current += 1;
      void speechEngine.stop();
    };
  }, [checkVoice]);

  const setSpeed = useCallback((nextSpeed: PolishSpeechRate) => {
    setSpeedState(nextSpeed);
    void storage.setItem(SPEED_STORAGE_KEY, String(nextSpeed));
  }, []);

  const setSettings = useCallback((next: PolishSpeechSettings) => {
    setSettingsState(next);
    void storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(next));
  }, []);

  const fragmentAt = useCallback((index: number) => {
    const source = queue.current;
    if (!source || index < 0 || index >= source.total) return undefined;
    if (index < batchStart.current || index >= batchStart.current + batch.current.length) {
      batchStart.current = index;
      batch.current = source.getBatch(index, BATCH_SIZE);
    }
    return batch.current[index - batchStart.current];
  }, []);

  const speakChunk = useCallback(
    (requestId: number) => {
      const fragment = fragmentAt(chunkIndex.current);
      if (!fragment || requestId !== generation.current) {
        setState('idle');
        return;
      }
      setCurrentIndex(chunkIndex.current);

      speechEngine.speak(fragment.text, {
        language: 'pl-PL',
        voice: voiceId.current,
        rate: speed,
        pitch: settings.pitch,
        onStart: () => {
          if (requestId === generation.current) setState('speaking');
        },
        onDone: () => {
          if (requestId !== generation.current) return;
          if (shouldStopAfterFragment(chunkIndex.current, stopAfterIndex.current)) {
            stopAfterIndex.current = null;
            setState('stopped');
            return;
          }
          chunkIndex.current += 1;
          speakChunk(requestId);
        },
        onStopped: () => {
          if (requestId === generation.current) {
            setError('Synteza mowy została zatrzymana przez system.');
            setState('stopped');
          }
        },
        onError: () => {
          if (requestId === generation.current) {
            setError(
              speechEngine.runtime === 'ios'
                ? 'Nie udało się odtworzyć syntezy mowy. Na iPhonie sprawdź także, czy tryb cichy jest wyłączony.'
                : 'Synteza mowy została przerwana przez system.'
            );
            setState('stopped');
          }
        },
      });
    },
    [fragmentAt, settings.pitch, speed]
  );

  const start = useCallback(
    async (nextQueue: PolishSpeechQueue, startIndex = 0, stopAtIndex?: number) => {
      const hasPolishVoice = voiceStatus === 'available' || (await checkVoice());
      if (!hasPolishVoice) {
        setError(
          'Na urządzeniu nie znaleziono polskiego głosu. Pobierz polski głos w ustawieniach syntezy mowy.'
        );
        return;
      }
      generation.current += 1;
      const requestId = generation.current;
      await speechEngine.stop();
      queue.current = nextQueue;
      batch.current = [];
      batchStart.current = 0;
      chunkIndex.current = Math.max(
        0,
        Math.min(startIndex, Math.max(nextQueue.total - 1, 0))
      );
      stopAfterIndex.current =
        typeof stopAtIndex === 'number' &&
        stopAtIndex >= chunkIndex.current &&
        stopAtIndex < nextQueue.total
          ? stopAtIndex
          : null;
      setCurrentIndex(chunkIndex.current);
      setTotal(nextQueue.total);
      setError(null);
      if (nextQueue.total === 0) {
        setState('idle');
        return;
      }
      speakChunk(requestId);
    },
    [checkVoice, speakChunk, voiceStatus]
  );

  const jumpTo = useCallback(
    async (index: number) => {
      const source = queue.current;
      if (!source || index < 0 || index >= source.total) return;
      generation.current += 1;
      const requestId = generation.current;
      await speechEngine.stop();
      stopAfterIndex.current = null;
      chunkIndex.current = index;
      setCurrentIndex(index);
      speakChunk(requestId);
    },
    [speakChunk]
  );

  const previous = useCallback(async () => {
    await jumpTo(Math.max(0, chunkIndex.current - 1));
  }, [jumpTo]);

  const next = useCallback(async () => {
    const source = queue.current;
    if (source) await jumpTo(Math.min(source.total - 1, chunkIndex.current + 1));
  }, [jumpTo]);

  const pause = useCallback(async () => {
    if (speechEngine.pauseMode === 'restart') {
      generation.current += 1;
      await speechEngine.stop();
      setState('paused');
      return;
    }
    try {
      await speechEngine.pause();
      setState('paused');
    } catch {
      setError('Pauza nie jest obsługiwana przez syntezę mowy na tym urządzeniu.');
    }
  }, []);

  const resume = useCallback(async () => {
    if (speechEngine.pauseMode === 'restart') {
      const requestId = ++generation.current;
      speakChunk(requestId);
      return;
    }
    try {
      await speechEngine.resume();
      setState('speaking');
    } catch {
      setError('Wznawianie nie jest obsługiwane przez syntezę mowy na tym urządzeniu.');
    }
  }, [speakChunk]);

  return {
    state,
    speed,
    voiceStatus,
    error,
    setSpeed,
    start,
    pause,
    resume,
    stop,
    previous,
    next,
    jumpTo,
    currentIndex,
    total,
    settings,
    setSettings,
  };
}
